/**
 * Pure formatting helpers for the venue Workshops-tab "UPCOMING HERE" table
 * (BON-168). Kept separate from the component so they're unit-testable and the
 * table file stays under the max-lines budget.
 */
import { formatVenueDate, formatVenueTime } from '../../../../utils/venueTime';

// The shared workshop-status vocabulary (BON-197) lives in a sibling module
// (`.tsx`, since the pill renders JSX). Re-exported here so callers that already
// import from `upcomingWorkshopsFormat` keep a stable import path. The
// dependency is one-directional (format -> status) to avoid an import cycle.
export {
  paceToStatus,
  resolveWorkshopStatus,
  WorkshopStatusPill,
  type StatusDescriptor,
  type WorkshopStatusInput,
} from './workshopStatus';

/** Shown in a table cell that has no value. */
export const EM_DASH = '—';

/** A crew member's display label. The BE already resolves each slot to a
 *  display name (or null when unassigned); normalize null/undefined to ''. */
export function crewName(name: string | null | undefined): string {
  return name ?? '';
}

/**
 * Date-chip formatters (BON-386). Every one of these takes the VENUE-LOCAL wall
 * clock (`Workshop.localStartDatetime`) — a naive ISO with no trailing `Z` — and
 * renders it with NO timezone conversion via `utils/venueTime`. Never pass the
 * absolute `startsAt`: its digits are the UTC instant, so two staff in different
 * states would read different times (and, near midnight, different days) for the
 * same workshop. A null local column renders as "—", never as the instant.
 */

/** Short date for the date chip, e.g. "Jul 9". */
export function chipDate(local: string | null | undefined): string {
  return formatVenueDate(local, 'MMM d');
}

/** Time-of-day shown next to the date chip, e.g. "6:30 PM". */
export function chipTime(local: string | null | undefined): string {
  return formatVenueTime(local, 'h:mm a');
}

/** Weekday abbreviation for the green date chip, e.g. "TUE". */
export function chipDow(local: string | null | undefined): string {
  return formatVenueDate(local, 'EEE').toUpperCase();
}

/** Day-of-month for the green date chip, e.g. "19". */
export function chipDayNum(local: string | null | undefined): string {
  return formatVenueDate(local, 'd');
}

/** Month abbreviation for the green date chip, e.g. "MAY". */
export function chipMon(local: string | null | undefined): string {
  return formatVenueDate(local, 'MMM').toUpperCase();
}
